import React from "react";
import { Link as RouterLink } from "react-router-dom";
import classNames from "classnames";

import { Membership } from "../../atoms/Icons/MembershipIcon";
import { Text } from "../../atoms/Text/Text";
import { Game } from "../../models/Game";
import { Ribbon } from "../../atoms/Ribbon/Ribbon";
import { ProgressiveImage } from "../../atoms/ProgressiveImage/ProgressiveImage";
import styles from "./Tiles.css";
import { Analytics } from "../../services/Analytics/Analytics";
import { AARPAnalytics } from "../../services/Analytics/AARPAnalytics";
import { RewardLabel } from "../../atoms/RewardLabel/RewardLabel";
import { SpainIcon } from "../../atoms/Icons/SpainIcon";
import { checkFullPath } from "../../../utils";

type GameTileProps = {
  game: Game;
  hardLink?: boolean;
  resizable?: boolean;
  promoBoxSlotTitle?: string;
  position?: number;
  key?: number;
};

export const DesktopTile = React.memo((props: GameTileProps) => {
  const { assetOriginUrl, secondaryThumbPath, thumbPath, membersOnly } =
    props.game;
  let { resizable } = props;
  resizable = true;
  const currentGame = new Game(props.game);
  const isSpain = currentGame.isSpain();

  const title = currentGame.seo_GameName;
  let vertical = false;
  if (resizable) {
    vertical = false;
  }

  return (
    <TileContainer
      className={classNames({
        [styles.resizableTileContainer]: !!resizable,
      })}
    >
      <TilePictureWrapper
        className={classNames({
          [styles.searchTilePictureWrapper]: !!resizable,
        })}
        data-element-description="tile picture"
      >
        <TilePicture
          title={`Enjoy playing ${title}`}
          alt={title}
          vertical={vertical}
          image={checkFullPath(assetOriginUrl, secondaryThumbPath || thumbPath)}
        />

        <Ribbon size="small" game={currentGame} />
        <RewardLabel size="small" game={currentGame} />

        {membersOnly ? (
          <MemberSign
            size="small"
            isStayingSharp={currentGame.isStayingSharp()}
          />
        ) : null}
      </TilePictureWrapper>
      <TextContainer
        data-element-description="tile title"
        {...(isSpain ? { lang: "es" } : null)}
      >
        {isSpain ? <SpainSign /> : null}

        <TileText
          className={classNames({
            [styles.searchTileText]: !!resizable,
          })}
          membersOnly={membersOnly}
          isSpain={isSpain}
        >
          {title}
        </TileText>
      </TextContainer>
    </TileContainer>
  );
});

DesktopTile.displayName = "DesktopTile";

export const GameTile = React.memo((props: GameTileProps) => {
  const trackGameClick = (
    game: Game,
    promoBoxSlotTitle: string,
    position: number
  ) => {
    if (promoBoxSlotTitle) {
      Analytics.trackEvent(
        Analytics.general.promoBoxSlot(game, promoBoxSlotTitle, position)
      );
    }
  };

  const Tile = DesktopTile;
  const dataElementDescription = "game tile";

  const { slug } = props.game;
  const { game } = props;

  return (
    <Link
      data-element-description={dataElementDescription}
      to={Game.getURLAlias(game)}
      href={game.seo_FinalURL}
      onClick={() =>
        trackGameClick(game, props.promoBoxSlotTitle, props.position)
      }
      isFakeGame={game.seo_IsFakeGame}
      {...AARPAnalytics.attr1Get(slug)}
      {...AARPAnalytics.attr2Get(slug)}
    >
      <Tile {...props} />
    </Link>
  );
});

GameTile.displayName = "GameTile";

const TileContainer = (props: any) => (
  <div
    {...props}
    className={classNames(props.className, styles.tileContainer)}
  />
);
TileContainer.displayName = "TileContainer";

const TilePictureWrapper = (props: any) => (
  <div
    {...props}
    className={classNames(props.className, styles.tilePictureWrapper)}
  />
);
const TilePicture = (props: any) => (
  <ProgressiveImage
    className={styles.tilePicture}
    loadWhenViewable={true}
    {...props}
  />
);

const TextContainer = (props: any) => (
  <div className={styles.tileTextContainer} {...props} />
);
TextContainer.displayName = "TextContainer";

const TileText = ({ membersOnly, ...props }) => (
  <Text {...props} className={classNames(props.className, styles.tileText)} />
);

TileText.displayName = "TileText";

export const Link = ({ isFakeGame, ...props }: any) => {
  return isFakeGame ? (
    <a className={styles.tileLink} {...props} target="_blank" rel="noopener" />
  ) : (
    <RouterLink className={styles.tileLink} {...props} />
  );
};

const MemberSign = (props: any) => (
  <Membership className={styles.memberSign} {...props} />
);
const SpainSign = (props: any) => (
  <SpainIcon className={styles.memberSign} {...props} />
);
